/* Property drawer — the real cross-surface contribution slice for one presence
 * property (pinned spec design/mockups/2026-07-16-client-hub/property-drawer.html).
 *
 * Replaces the legacy asset drawer (ui/drawer.ts) on presence-map node clicks.
 * Structure, verbatim from the spec: header (name/url · control chip · status ·
 * verified · connectivity) → Contribution (measured Search positions per term,
 * AI citations, News honest "not wired") + a measured rollup → Modeled
 * suggestion (advisory, visually distinct, NEVER a metric) → Extracted fields
 * (Crawl4AI unbuilt → honest "not extracted") → Connectivity (inbound/outbound +
 * missing bridges from the link graph) → Attached work (real tasks for THIS
 * asset via task.asset_id) → provenance footer.
 *
 * HONESTY (spec §11): every number traces to the loaded payload's real data
 * (contribution.ts, the SERP captures, the link graph, the task ledger). News is
 * never a number. Crawl4AI field extraction is unbuilt, so that section renders
 * its honest absent state, never fabricated fields. The mockup's fixture numbers
 * are never copied.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { Workspace } from '../../state/workspace.ts';
import type { NormalizedAsset, SerpCapture } from '../../types/contract.ts';
import { computeContribution, contributionFor, type ContributionMap } from '../../derive/contribution.ts';
import { domainOf } from '../../lib/format.ts';
import { brandVisual } from '../../lib/identity.ts';
import { brandLogo } from '../register.ts';
import { currentRoute } from '../../lib/router.ts';
import type { Unsubscribe } from '../../state/store.ts';
import {
  extractableUrls,
  extractionState,
  recordForUrl,
  runExtraction,
  subscribeExtraction,
} from '../../state/pageFields.ts';
import type { IdentityField, PageFieldRecord } from '../../api/pageFields.ts';

let escHandler: ((e: KeyboardEvent) => void) | null = null;
/** Context for the currently-open drawer, so a store change (the field-
 * extraction pass settling) can re-render it in place. */
let openCtx: { state: AppPayloadState; ws: Workspace; assetId: string } | null = null;
let unsubExtraction: Unsubscribe | null = null;

function hostEl(): HTMLElement {
  let el = document.getElementById('propDrawer');
  if (!el) { el = document.createElement('div'); el.id = 'propDrawer'; document.body.appendChild(el); }
  return el;
}

export function closePropertyDrawer(): void {
  const el = document.getElementById('propDrawer');
  if (el) render(nothing, el);
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  if (unsubExtraction) { unsubExtraction(); unsubExtraction = null; }
  openCtx = null;
}

/** Re-render the open drawer from its remembered context (used when the field-
 * extraction store settles). No-op if the drawer is closed. */
function repaintDrawer(): void {
  if (!openCtx) return;
  const a = openCtx.state.assets.find((x) => x.id === openCtx?.assetId);
  if (!a) return;
  render(drawerView(openCtx.state, openCtx.ws, a, computeContribution(openCtx.state)), hostEl());
}

/** Open the property drawer for `assetId`, built from the loaded run's real data. */
export function openPropertyDrawer(state: AppPayloadState, ws: Workspace, assetId: string): void {
  const a = state.assets.find((x) => x.id === assetId);
  if (!a) return;
  openCtx = { state, ws, assetId };
  const contrib = computeContribution(state);
  render(drawerView(state, ws, a, contrib), hostEl());
  if (!escHandler) {
    escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePropertyDrawer(); };
    document.addEventListener('keydown', escHandler);
  }
  if (!unsubExtraction) unsubExtraction = subscribeExtraction(() => repaintDrawer());
}

/* ── derivations ───────────────────────────────────────────────────────────── */

const CTRL_TIER: Record<string, string> = { owned: 'owned', editable: 'editable', claimable: 'claimable', media: 'media' };

function allCaptures(state: AppPayloadState): SerpCapture[] {
  const out: SerpCapture[] = [];
  if (state.serp) out.push(state.serp);
  for (const s of state.serps || []) out.push(s);
  return out;
}

interface TermPos { term: string; page: number; pos: number; }

/** Best SERP position per tracked term for this asset's domain (measured). */
function searchByTerm(state: AppPayloadState, a: NormalizedAsset): { terms: TermPos[]; trackedTerms: number } {
  const d = domainOf(a.url);
  const caps = allCaptures(state);
  const trackedTerms = new Set(caps.map((c) => c.query).filter(Boolean)).size;
  if (!d) return { terms: [], trackedTerms };
  const byTerm = new Map<string, TermPos>();
  for (const cap of caps) {
    const term = cap.query || '';
    for (const r of cap.results || []) {
      if (domainOf(r.url) !== d) continue;
      // W1-B: page may be null (unknown membership) — treat as worst so a known
      // page always wins the "best occurrence" pick; never assert a false page 1.
      const rp = r.page ?? 99;
      const cur = byTerm.get(term);
      if (!cur || rp < cur.page || (rp === cur.page && r.rank < cur.pos)) {
        byTerm.set(term, { term, page: rp, pos: r.rank });
      }
    }
  }
  return { terms: [...byTerm.values()], trackedTerms };
}

interface Connectivity { inbound: string[]; outbound: string[]; bridges: string[]; }

function connectivity(state: AppPayloadState, a: NormalizedAsset): Connectivity {
  const nameOf = (id: string): string => state.assets.find((x) => x.id === id)?.title || id;
  const inbound: string[] = [];
  const outbound: string[] = [];
  for (const e of state.edges || []) {
    if (e.to === a.id) inbound.push(nameOf(e.from));
    if (e.from === a.id) outbound.push(nameOf(e.to));
  }
  const bridges: string[] = [];
  for (const b of state.linkGraph?.bridges || []) {
    if (b.assetId === a.id) bridges.push(...(b.missing || []));
  }
  if ((state.linkGraph?.orphans || []).some((o) => o.assetId === a.id)) {
    bridges.push('orphaned — no inbound links measured');
  }
  return { inbound, outbound, bridges };
}

/** This asset's measured share of the client's OWNED surfaced reputation
 * (search + AI contribution), as a %. Null when there's no owned base to divide
 * by — honest "not measured", never a fabricated 0. */
function ownedShare(state: AppPayloadState, contrib: ContributionMap, a: NormalizedAsset): number | null {
  const weight = (id: string): number => {
    const c = contributionFor(contrib, id);
    return c ? c.searchAppearances + c.aiCitations : 0;
  };
  let ownedTotal = 0;
  for (const x of state.assets) {
    if (x.category === 'owned' || x.category === 'editable') ownedTotal += weight(x.id);
  }
  if (ownedTotal <= 0) return null;
  const mine = weight(a.id);
  if (mine <= 0) return null;
  return Math.round((100 * mine) / ownedTotal);
}

/** Modeled (advisory) suggestion from the link graph — NEVER a metric. Null when
 * there's nothing to suggest (honest — the section is omitted). */
function suggestion(state: AppPayloadState, a: NormalizedAsset): string | null {
  const bridge = (state.linkGraph?.bridges || []).find((b) => b.assetId === a.id);
  if (bridge && bridge.fixes?.length) return bridge.fixes[0]!;
  if ((state.linkGraph?.orphans || []).some((o) => o.assetId === a.id)) {
    return 'This property is orphaned from the entity link graph — adding it to the entity’s sameAs / linking it from the primary site would strengthen how it surfaces in AI answers.';
  }
  return null;
}

// DESIGN-FIDELITY P0-3 (sweep): a property's head icon obeys the same brand-
// visual contract as everything else — domain favicon → neutral NON-LETTER mark,
// never a two-letter monogram.
function propertyVisual(a: NormalizedAsset) {
  return brandVisual({ domain: domainOf(a.url) });
}

/* ── extracted fields (measured, W2-E) ─────────────────────────────────────── */
const DISPLAY_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'hours', label: 'Hours' },
  { key: 'descriptionBio', label: 'Description' },
  { key: 'websiteLink', label: 'Website' },
];

function fieldRow(label: string, f: IdentityField | undefined): TemplateResult {
  if (!f || f.status === 'unverifiable') {
    return html`<div class="pd-field"><span class="pd-field-label">${label}</span>
      <span class="pd-field-val pd-field-unver">unverifiable</span></div>`;
  }
  if (f.status === 'not-observed') {
    return html`<div class="pd-field"><span class="pd-field-label">${label}</span>
      <span class="pd-field-val pd-field-none">not shown on this page</span></div>`;
  }
  const value = Array.isArray(f.value) ? f.value.join(' · ') : f.value || '';
  return html`<div class="pd-field"><span class="pd-field-label">${label}</span>
    <span class="pd-field-val"><b>${value}</b>${
      typeof f.confidence === 'number'
        ? html`<small class="pd-field-conf">confidence ${(f.confidence * 100).toFixed(0)}%</small>`
        : nothing
    }</span></div>`;
}

function extractedRecordView(rec: PageFieldRecord): TemplateResult {
  if (rec.status === 'unverifiable') {
    const reason = rec.receipt?.reason || 'the page could not be fetched or read';
    return html`<p class="pd-empty">Unverifiable — ${reason}. This property's fields could not be measured;
      nothing is guessed here.</p>`;
  }
  const via = Object.values(rec.identityFields).find((f) => f.extractedVia)?.extractedVia || 'crawl4ai';
  return html`<div class="pd-fields">
    ${DISPLAY_FIELDS.map((d) => fieldRow(d.label, rec.identityFields[d.key]))}
    <div class="pd-field-src">measured — displayed values only, via ${via}. Absent fields state "not shown", not a guess.</div>
  </div>`;
}

function extractedFields(state: AppPayloadState, a: NormalizedAsset): TemplateResult {
  const ex = extractionState();
  const slug = currentRoute().slug || '';
  if (ex.status === 'loading') {
    return html`<p class="pd-empty">Measuring displayed fields across this client's properties…</p>`;
  }
  const rec = recordForUrl(a.url);
  if (rec) return extractedRecordView(rec);
  if (ex.status === 'error') {
    return html`<p class="pd-empty">Field extraction is unavailable — ${ex.reason}. Nothing is fabricated here.</p>`;
  }
  const canRun = Boolean(slug) && Boolean(a.url);
  const note =
    ex.status === 'ok'
      ? 'This property was not covered by the last extraction pass.'
      : 'The Crawl4AI pass (name / address / phone / hours / description, with measured conflicts) has not run for this property. Nothing is fabricated here.';
  return html`<p class="pd-empty">${note}</p>
    ${canRun
      ? html`<button class="pd-extract-btn"
          @click=${() => runExtraction(slug, [a.url as string, ...extractableUrls(state)])}>
          Run field extraction →</button>`
      : nothing}`;
}

/* ── view ──────────────────────────────────────────────────────────────────── */

function drawerView(state: AppPayloadState, ws: Workspace, a: NormalizedAsset, contrib: ContributionMap): TemplateResult {
  const c = contributionFor(contrib, a.id);
  const tier = CTRL_TIER[a.category] || 'community';
  const { terms, trackedTerms } = searchByTerm(state, a);
  const bestTerm = terms.slice().sort((x, y) => x.page - y.page || x.pos - y.pos)[0] || null;
  const conn = connectivity(state, a);
  // FIX-audit F11: "orphan" vs "linked (measured inbound)" is only meaningful
  // when a link graph was actually measured. Without one, neither claim is true.
  const linkMeasured = state.linkGraph != null;
  const isOrphan = (state.linkGraph?.orphans || []).some((o) => o.assetId === a.id);
  const share = ownedShare(state, contrib, a);
  const sugg = suggestion(state, a);
  const tasks = ws.available === true ? ws.tasks.filter((t) => t.asset_id === a.id) : [];
  const aiDomains = c?.aiDomains ? [...new Set(c.aiDomains)] : [];

  // FIX-audit F10: a plane that didn't run has no denominator — its metric reads
  // "not run", NEVER "not on any tracked page one" / "not cited" (both assert a
  // measurement that never happened). `trackedTerms` = 0 ⇒ no Search plane;
  // `state.aiNarrative == null` ⇒ no AI plane.
  const searchRan = trackedTerms > 0;
  const aiRan = state.aiNarrative != null;

  const searchRow = html`<div class="pd-contrib-row">
    <div class="pd-contrib-head"><span class="pd-contrib-label">Google Search</span>
      ${bestTerm ? html`<span class="pd-contrib-metric">Page-${bestTerm.page} · pos ${bestTerm.pos}</span>`
        : html`<span class="pd-contrib-metric muted">${searchRan ? 'not on any tracked page one' : 'Search not run'}</span>`}</div>
    ${terms.length
      ? html`<p class="pd-note">appears for ${terms.length} of ${trackedTerms} tracked term${trackedTerms === 1 ? '' : 's'}</p>
        <div class="pd-term-list">${terms.map((t) => html`<div class="pd-term-row"><span>${t.term}</span><strong>P${t.page} · pos ${t.pos}</strong></div>`)}</div>`
      : html`<p class="pd-note muted">${searchRan ? 'not observed for any tracked term' : 'no Search capture on this run'}</p>`}
  </div>`;

  const aiRow = html`<div class="pd-contrib-row">
    <div class="pd-contrib-head"><span class="pd-contrib-label">AI (AIQ)</span>
      ${c && c.aiCitations > 0 ? html`<span class="pd-contrib-metric">${c.aiCitations} citation${c.aiCitations === 1 ? '' : 's'}</span>`
        : html`<span class="pd-contrib-metric muted">${aiRan ? 'not cited' : 'AI narrative not run'}</span>`}</div>
    ${aiDomains.length
      ? html`<p class="pd-note">cited via ${aiDomains.join(', ')}</p>`
      : html`<p class="pd-note muted">${aiRan ? 'no measured AI citations map to this property' : 'no AIQ plane on this run'}</p>`}
  </div>`;

  const newsRow = html`<div class="pd-contrib-row">
    <div class="pd-contrib-head"><span class="pd-contrib-label">Google News</span>
      <span class="pd-contrib-metric muted">not wired</span></div>
    <p class="pd-note muted">IMPACT News has no data plane on this run — never a fabricated count.</p>
  </div>`;

  const rollup = share != null
    ? html`<div class="pd-rollup"><span class="fb-conn-dot" aria-hidden="true"></span>drives an estimated <b>${share}%</b> of this client’s owned surfaced reputation (measured).</div>`
    : html`<div class="pd-rollup muted"><span class="fb-conn-dot orphan" aria-hidden="true"></span>owned-reputation share not measured for this property this run.</div>`;

  return html`
    <div class="pd-scrim" @click=${closePropertyDrawer}></div>
    <aside class="pd-drawer" role="dialog" aria-label=${`Property detail: ${a.title || a.url || a.id}`}>
      <div class="pd-scroll">
        <div class="pd-head">
          <span class="pd-head-icon">${brandLogo(propertyVisual(a))}</span>
          <div class="pd-head-main">
            <div class="pd-head-title">${a.title || a.url || a.id}</div>
            ${a.url ? html`<div class="pd-head-url">${domainOf(a.url) || a.url}</div>` : nothing}
            <div class="pd-head-chips">
              <span class="fb-ctrl-chip ${tier}">${tier}</span>
              ${a.status ? html`<span class="fb-chip ${a.status === 'active' || a.status === 'claimed' ? 'green' : a.status === 'unclaimed' ? 'neg' : 'amber'}">${a.status}</span>` : nothing}
              ${a.verified ? html`<span class="fb-chip green">verified ✓</span>` : html`<span class="fb-chip">unverified</span>`}
            </div>
            <div class="pd-head-conn"><span class="fb-conn-dot ${!linkMeasured ? '' : isOrphan ? 'orphan' : ''}" aria-hidden="true"></span>${!linkMeasured ? 'link graph not measured this run' : isOrphan ? 'orphan (no inbound links measured)' : 'linked (measured inbound)'}</div>
          </div>
          <div class="pd-head-actions"><button class="pd-icon-btn" title="Close" @click=${closePropertyDrawer}>✕</button></div>
        </div>

        <div class="pd-section">
          <div class="pd-section-title"><span class="pd-eyebrow">Contribution — how this property surfaces</span><span class="pd-src-note">measured, cross-surface</span></div>
          ${searchRow}${aiRow}${newsRow}${rollup}
        </div>

        ${sugg ? html`<div class="pd-suggestion">
          <span class="pd-suggestion-label">Modeled suggestion · advisory, not a metric</span>
          <p class="pd-suggestion-body">${sugg} <i>(model estimate — the AM decides)</i></p>
        </div>` : nothing}

        <div class="pd-section">
          <div class="pd-section-title"><span class="pd-eyebrow">Extracted fields</span><span class="pd-src-note">Crawl4AI field-extraction pass · measured</span></div>
          ${extractedFields(state, a)}
        </div>

        <div class="pd-section">
          <div class="pd-section-title"><span class="pd-eyebrow">Connectivity</span><span class="pd-src-note">link graph</span></div>
          ${conn.inbound.length || conn.outbound.length || conn.bridges.length
            ? html`<div class="pd-conn-list">
                ${conn.inbound.length ? html`<div class="pd-conn-row"><b>inbound</b>${conn.inbound.slice(0, 4).join(', ')}${conn.inbound.length > 4 ? ` +${conn.inbound.length - 4} more` : ''}</div>` : nothing}
                ${conn.outbound.length ? html`<div class="pd-conn-row"><b>outbound</b>${conn.outbound.slice(0, 4).join(', ')}${conn.outbound.length > 4 ? ` +${conn.outbound.length - 4} more` : ''}</div>` : nothing}
                ${conn.bridges.map((b) => html`<div class="pd-conn-row bridge"><b>bridge</b>missing: ${b}</div>`)}
              </div>`
            : html`<p class="pd-empty">No measured links to or from this property on this run.</p>`}
        </div>

        <div class="pd-section">
          <div class="pd-section-title"><span class="pd-eyebrow">Attached work</span><span class="pd-src-note">task ledger</span></div>
          ${tasks.length
            ? html`<div class="pd-tasks">${tasks.map((t) => html`<div class="pd-task-row">
                <span class="fb-conn-dot" aria-hidden="true"></span>
                <span class="pd-task-text">${t.title}</span>
                <span class="pd-task-state">${ws.STATUS_LABEL[t.status] || t.status}</span>
              </div>`)}</div>`
            : html`<p class="pd-empty">No task is attached to this property yet.</p>`}
          <div class="pd-add-task-row">
            ${currentRoute().slug ? html`<a class="pd-add-task-btn" href=${`#/client/${currentRoute().slug}/board`} @click=${closePropertyDrawer}>Open the board to add work →</a>` : nothing}
            <span class="pd-gate-note">evidence-gated — Verified needs a probe, not a click</span>
          </div>
        </div>
      </div>
      <footer class="pd-provenance">
        <span>${a.verified ? 'verified this run' : 'not probe-verified'} · field crawl not wired</span>
        ${state.runFile ? html`<span class="pd-src-receipt">run ${state.runFile}</span>` : nothing}
      </footer>
    </aside>`;
}
